import React, { useEffect, useMemo, useState } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { GridColDef } from '@mui/x-data-grid';
import { useCurrentUser } from '../hooks/useCurrentUser';
import apiClient, { usersApi, DbUser } from '../api/client';
import { canEditPage } from '../utils/rbac';
import { titleCase } from '../utils/format';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { PageHeader } from './layout/PageHeader';
import { PageLoader } from './PageLoader';
import { StatCard } from './layout/StatCard';
import { TableCard } from './layout/TableCard';
import { ResizableDataGrid } from './grid/ResizableDataGrid';
import { ChartCard } from './charts/ChartCard';
import { NexusBarChart, NexusDonut, NexusTrend } from './charts/NexusCharts';
import { tokens } from '../theme/tokens';
import { ppmProjectsApi, PpmProjectListItem } from '../api/ppm';

const pretty = (v: any) => JSON.stringify(v ?? {}, null, 2);

const TimesheetsPage: React.FC = () => {
  const { dbUser } = useCurrentUser();
  const canEdit = canEditPage(dbUser, 'timesheets');

  const [rows, setRows] = useState<any[]>([]);
  const [projects, setProjects] = useState<PpmProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meId, setMeId] = useState<string>('');
  const [meUser, setMeUser] = useState<DbUser | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [jsonText, setJsonText] = useState('{}');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitRange, setSubmitRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 6);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    return { startDate: iso(start), endDate: iso(end) };
  });

  const [form, setForm] = useState({
    userId: 'demo_user_dev',
    projectId: 'demo_project_ehr',
    date: new Date().toISOString().slice(0, 10),
    hours: '7.5',
    billable: true,
    status: 'DRAFT',
    description: '',
  });

  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string }>({ severity: 'success', message: '' });

  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [actionsRow, setActionsRow] = useState<any | null>(null);
  const actionsOpen = Boolean(actionsAnchorEl);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [topPanel, setTopPanel] = useState<null | 'entries' | 'totalHours' | 'billable' | 'submitted'>(null);
  const [showAllPanel, setShowAllPanel] = useState(false);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const [res, pRes, meRes] = await Promise.all([
        apiClient.get('/timesheets'),
        ppmProjectsApi.getAll({ source: 'internal' }).catch(() => ({ data: [] as any[] })),
        usersApi.me().catch(() => ({ data: null as any })),
      ]);
      setRows(res.data || []);
      setProjects((pRes.data || []) as any);
      const meData = (meRes as any)?.data;
      const myId = String(meData?.id || '');
      setMeId(myId);
      setMeUser(meData || null);
      if (!canEdit && myId) {
        setForm((f) => ({ ...f, userId: myId }));
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cols: GridColDef[] = useMemo(() => {
    const base: GridColDef[] = [
      { field: 'id', headerName: 'ID', width: 220 },
      ...(canEdit
        ? ([
            {
              field: 'user',
              headerName: 'User',
              width: 240,
              valueGetter: (p) => p.row?.User?.displayName || p.row?.User?.username || p.row?.User?.email || p.row?.userId || '—',
            },
          ] as GridColDef[])
        : []),
      { field: 'projectId', headerName: 'Project', width: 240, valueGetter: (p) => p.row?.projectId ?? '—' },
      { field: 'date', headerName: 'Date', width: 140, valueGetter: (p) => (p.row?.date ? String(p.row.date).slice(0, 10) : '—') },
      { field: 'hours', headerName: 'Hours', width: 110, valueGetter: (p) => p.row?.hours ?? '—' },
      { field: 'billable', headerName: 'Billable', width: 110, valueGetter: (p) => (p.row?.billable ? 'Yes' : 'No') },
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        valueGetter: (p) => (p.row?.status ? titleCase(String(p.row.status)) : '—'),
      },
      { field: 'description', headerName: 'Description', flex: 1, minWidth: 260, valueGetter: (p) => p.row?.description ?? '—' },
    ];

    if (!canEdit) return base;

    return [
      ...base,
      {
        field: 'actions',
        headerName: 'Admin',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setActionsAnchorEl(e.currentTarget);
              setActionsRow(params.row);
            }}
            aria-label="Actions"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      },
    ];
  }, [canEdit]);

  const summary = useMemo(() => {
    const total = rows.length;
    const totalHours = rows.reduce((acc, r) => acc + Number(r.hours || 0), 0);
    const billableHours = rows.reduce((acc, r) => acc + (r.billable ? Number(r.hours || 0) : 0), 0);
    const submitted = rows.filter((r) => String(r.status || '').toUpperCase() === 'SUBMITTED').length;
    return { total, totalHours, billableHours, submitted };
  }, [rows]);

  const statusHours = useMemo(() => {
    const map = new Map<string, number>();
    (rows || []).forEach((r: any) => {
      const s = String(r.status || 'UNKNOWN').toUpperCase();
      map.set(s, (map.get(s) || 0) + Number(r.hours || 0));
    });
    return Array.from(map.entries())
      .map(([name, value]) => ({ name, value: Number(value || 0) }))
      .sort((a, b) => b.value - a.value);
  }, [rows]);

  const billableMix = useMemo(() => {
    const bill = Number(summary.billableHours || 0);
    const non = Math.max(0, Number(summary.totalHours || 0) - bill);
    return [
      { name: 'Billable', value: bill },
      { name: 'Non-billable', value: non },
    ];
  }, [summary.billableHours, summary.totalHours]);

  const hoursByDay = useMemo(() => {
    const map = new Map<string, number>();
    (rows || []).forEach((r: any) => {
      const d = r.date ? String(r.date).slice(0, 10) : '—';
      if (d === '—') return;
      map.set(d, (map.get(d) || 0) + Number(r.hours || 0));
    });
    return Array.from(map.entries())
      .map(([date, hours]) => ({ date, hours: Number(hours || 0) }))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-14);
  }, [rows]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      userId: meId || 'demo_user_dev',
      projectId: (projects[0]?.id ? String(projects[0].id) : '') || 'demo_project_ehr',
      date: new Date().toISOString().slice(0, 10),
      hours: '7.5',
      billable: true,
      status: 'DRAFT',
      description: '',
    });
    setJsonText(pretty({}));
    setShowAdvanced(false);
    setEditOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setForm({
      userId: row.userId || 'demo_user_dev',
      projectId: row.projectId || projects[0]?.id || 'demo_project_ehr',
      date: row.date ? String(row.date).slice(0, 10) : new Date().toISOString().slice(0, 10),
      hours: row.hours != null ? String(row.hours) : '0',
      billable: !!row.billable,
      status: row.status || 'DRAFT',
      description: row.description || '',
    });
    setJsonText(pretty(row));
    setShowAdvanced(false);
    setEditOpen(true);
  };

  const save = async () => {
    try {
      if (!form.projectId?.trim()) {
        setToast({ severity: 'error', message: 'Project is required.' });
        setToastOpen(true);
        return;
      }
      const payload: any = {
        userId: canEdit ? form.userId.trim() : (meId || form.userId.trim()),
        projectId: form.projectId.trim(),
        date: new Date(`${form.date}T00:00:00.000Z`),
        hours: Number(form.hours),
        billable: !!form.billable,
        status: canEdit ? form.status : 'DRAFT',
        description: form.description.trim() ? form.description.trim() : undefined,
      };

      if (showAdvanced) {
        // Allow advanced override if user edited JSON
        try {
          const advanced = JSON.parse(jsonText || '{}');
          Object.assign(payload, advanced);
        } catch {
          // ignore invalid JSON and just use form payload
        }
      }

      // Enforce project attribution even if Advanced JSON tries to remove/blank it.
      if (!payload.projectId || !String(payload.projectId).trim()) {
        setToast({ severity: 'error', message: 'Project is required.' });
        setToastOpen(true);
        return;
      }
      if (editing?.id) {
        await apiClient.put(`/timesheets/${editing.id}`, payload);
        setToast({ severity: 'success', message: 'Updated' });
      } else {
        await apiClient.post('/timesheets', payload);
        setToast({ severity: 'success', message: 'Created' });
      }
      setToastOpen(true);
      setEditOpen(false);
      refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.message || 'Save failed (invalid JSON?)' });
      setToastOpen(true);
    }
  };

  return (
    <PageLoader loading={loading}>
      <Container maxWidth="xl" sx={{ pt: 1, pb: 3 }}>
      <PageHeader
        title="Timesheets"
        subtitle="Track time entries and approvals."
        actions={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={refresh} sx={{ borderRadius: 2 }}>
              Refresh
            </Button>
            {!canEdit ? (
              <Button variant="outlined" onClick={() => setSubmitOpen(true)} sx={{ borderRadius: 2 }}>
                Submit for approval
              </Button>
            ) : null}
            <Button variant="contained" onClick={openCreate} sx={{ borderRadius: 2 }}>
              New Entry
            </Button>
          </Stack>
        }
      />

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Entries"
            value={summary.total}
            subtitle="Loaded"
            accent={tokens.color.accent}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'entries' ? null : 'entries'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Hours"
            value={summary.totalHours.toFixed(1)}
            subtitle="All time"
            accent={tokens.color.accent}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'totalHours' ? null : 'totalHours'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Billable Hours"
            value={summary.billableHours.toFixed(1)}
            subtitle="Billable"
            accent={tokens.color.success}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'billable' ? null : 'billable'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Submitted"
            value={summary.submitted}
            subtitle="Pending approval"
            accent={tokens.color.warning}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'submitted' ? null : 'submitted'));
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Collapse in={!!topPanel} unmountOnExit>
            <TableCard
              title={topPanel === 'entries' ? 'Recent entries' : topPanel === 'totalHours' ? 'Largest time entries' : topPanel === 'billable' ? 'Billable entries' : 'Submitted entries'}
              actions={
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined" onClick={() => setShowAllPanel((s) => !s)} sx={{ borderRadius: 2 }}>
                    {showAllPanel ? 'Show less' : 'Show all'}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setTopPanel(null)} sx={{ borderRadius: 2 }}>
                    Close
                  </Button>
                </Stack>
              }
            >
              {(() => {
                const byDateDesc = (a: any, b: any) => String(b.date || '').localeCompare(String(a.date || ''));
                const byHoursDesc = (a: any, b: any) => Number(b.hours || 0) - Number(a.hours || 0);

                const list =
                  topPanel === 'entries'
                    ? rows.slice().sort(byDateDesc)
                    : topPanel === 'totalHours'
                      ? rows.slice().sort(byHoursDesc)
                      : topPanel === 'billable'
                        ? rows.filter((r) => !!r.billable).slice().sort(byDateDesc)
                        : rows.filter((r) => String(r.status || '').toUpperCase() === 'SUBMITTED').slice().sort(byDateDesc);

                if (!list.length) {
                  return (
                    <Alert severity="info" variant="outlined">
                      No results.
                    </Alert>
                  );
                }

                return (
                  <Stack spacing={1}>
                    {list.slice(0, showAllPanel ? 1000 : 5).map((r: any) => (
                      <Box
                        key={r.id}
                        sx={{ p: 1, borderRadius: `${tokens.radius.sm}px`, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                        onClick={() => {
                          setActionsRow(r);
                          setDetailsOpen(true);
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                          <Typography sx={{ fontWeight: 650, minWidth: 0 }} noWrap title={String(r.description || '')}>
                            {r.projectId || '—'} • {String(r.date || '').slice(0, 10) || '—'}
                          </Typography>
                          <Chip size="small" label={`${Number(r.hours || 0).toFixed(1)}h`} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          user {r.userId || '—'} • {r.billable ? 'billable' : 'non-billable'} • {r.status ? titleCase(String(r.status)) : '—'}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                );
              })()}
            </TableCard>
          </Collapse>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <ChartCard title="Billable split" subtitle="Hours breakdown" height={260}>
            <NexusDonut
              height={200}
              centerLabel="Hours"
              centerValue={summary.totalHours.toFixed(1)}
              valueFormatter={(v) => `${Number(v || 0).toFixed(1)}h`}
              data={[
                { name: 'Billable', value: billableMix[0].value, color: tokens.color.success },
                { name: 'Non-billable', value: billableMix[1].value, color: tokens.color.neutralSoft },
              ]}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Hours by status" subtitle="Sum of hours per workflow state" height={260}>
            <NexusBarChart
              data={statusHours}
              xKey="name"
              height={220}
              series={[{ key: 'value', name: 'Hours' }]}
              valueFormatter={(v) => `${Number(v || 0).toFixed(1)}h`}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Last 14 days" subtitle="Total hours per day" height={260}>
            <NexusTrend
              data={hoursByDay}
              xKey="date"
              area
              height={220}
              series={[{ key: 'hours', name: 'Hours' }]}
              valueFormatter={(v) => `${Number(v || 0).toFixed(1)}h`}
            />
          </ChartCard>
        </Grid>
      </Grid>

      <TableCard title="Timesheet entries" subtitle="Click a row for details. Admin actions are in the overflow menu." actions={loading ? <CircularProgress size={18} /> : null}>
        <div style={{ height: 600, width: '100%' }}>
          <ResizableDataGrid
            storageKey="gridWidths.timesheets"
            enableDensityControls
            rows={rows}
            columns={cols}
            getRowId={(r) => r.id}
            disableRowSelectionOnClick
            onRowClick={(p) => {
              setActionsRow(p.row);
              setDetailsOpen(true);
            }}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': { backgroundColor: tokens.color.surfaceMuted },
              '& .MuiDataGrid-row:hover': { backgroundColor: tokens.color.surfaceMuted },
            }}
          />
        </div>
      </TableCard>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? 'Edit Entry' : 'New Entry'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25, mt: 1 }}>
            {canEdit ? (
              <TextField label="User ID" value={form.userId} onChange={(e) => setForm((f) => ({ ...f, userId: e.target.value }))} fullWidth />
            ) : (
              <TextField 
                label="User" 
                value={meUser?.displayName || meUser?.username || meUser?.email || '—'} 
                fullWidth 
                disabled 
                helperText="Your name (cannot be changed)"
              />
            )}
            <TextField
              label="Project"
              value={form.projectId}
              onChange={(e) => setForm((f) => ({ ...f, projectId: e.target.value }))}
              select
              fullWidth
              required
            >
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="Hours" value={form.hours} onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))} fullWidth />
            {canEdit ? (
              <TextField
                label="Status"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                select
                fullWidth
              >
                {['DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED'].map((s) => (
                  <MenuItem key={s} value={s}>
                    {titleCase(s)}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            <FormControlLabel
              control={<Switch checked={form.billable} onChange={(e) => setForm((f) => ({ ...f, billable: e.target.checked }))} />}
              label="Billable"
            />
          </Box>
          <TextField
            sx={{ mt: 1.25 }}
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />

          <Accordion sx={{ mt: 1.25 }} expanded={showAdvanced} onChange={(_, v) => setShowAdvanced(v)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>Advanced (JSON override)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                fullWidth
                multiline
                minRows={10}
                sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
              />
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={submitOpen} onClose={() => setSubmitOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Submit for approval</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>
          <TextField
            label="Start date"
            type="date"
            value={submitRange.startDate}
            onChange={(e) => setSubmitRange((r) => ({ ...r, startDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End date"
            type="date"
            value={submitRange.endDate}
            onChange={(e) => setSubmitRange((r) => ({ ...r, endDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <Alert severity="info" variant="outlined">
            This submits your <b>draft</b> entries in the selected range.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setSubmitOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                await apiClient.put('/timesheets/submit-for-approval', {
                  userId: meId || undefined,
                  startDate: submitRange.startDate,
                  endDate: submitRange.endDate,
                });
                setToast({ severity: 'success', message: 'Submitted for approval' });
                setToastOpen(true);
                setSubmitOpen(false);
                refresh();
              } catch (e: any) {
                setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Submit failed' });
                setToastOpen(true);
              }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toastOpen} autoHideDuration={2500} onClose={() => setToastOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>

      <Menu
        anchorEl={actionsAnchorEl}
        open={actionsOpen}
        onClose={() => {
          setActionsAnchorEl(null);
          setActionsRow(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (actionsRow) openEdit(actionsRow);
            setActionsAnchorEl(null);
          }}
        >
          Edit
        </MenuItem>
        {canEdit ? (
          <>
            <MenuItem
              onClick={async () => {
                if (!actionsRow) return;
                try {
                  await apiClient.put(`/timesheets/${actionsRow.id}`, { status: 'APPROVED', approvedBy: 'demo_user_pm', approvedAt: new Date() });
                  setToast({ severity: 'success', message: 'Approved' });
                  setToastOpen(true);
                  refresh();
                } catch (e: any) {
                  setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Approve failed' });
                  setToastOpen(true);
                } finally {
                  setActionsAnchorEl(null);
                }
              }}
            >
              Approve
            </MenuItem>
            <MenuItem
              onClick={async () => {
                if (!actionsRow) return;
                try {
                  await apiClient.put(`/timesheets/${actionsRow.id}`, { status: 'REJECTED', approvedBy: 'demo_user_pm', approvedAt: new Date() });
                  setToast({ severity: 'success', message: 'Rejected' });
                  setToastOpen(true);
                  refresh();
                } catch (e: any) {
                  setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Reject failed' });
                  setToastOpen(true);
                } finally {
                  setActionsAnchorEl(null);
                }
              }}
            >
              Reject
            </MenuItem>
          </>
        ) : null}
        <MenuItem
          onClick={async () => {
            if (!actionsRow) return;
            // eslint-disable-next-line no-alert
            const ok = window.confirm('Delete timesheet entry?');
            if (!ok) return;
            try {
              await apiClient.delete(`/timesheets/${actionsRow.id}`);
              setToast({ severity: 'success', message: 'Deleted' });
              setToastOpen(true);
              refresh();
            } catch (e: any) {
              setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Delete failed' });
              setToastOpen(true);
            } finally {
              setActionsAnchorEl(null);
            }
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Timesheet entry</DialogTitle>
        <DialogContent>
          {actionsRow ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={actionsRow.status ? titleCase(String(actionsRow.status)) : '—'} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                <Chip size="small" label={actionsRow.billable ? 'Billable' : 'Non-billable'} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                <Chip size="small" label={actionsRow.date ? String(actionsRow.date).slice(0, 10) : '—'} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 650 }}>
                {actionsRow.hours != null ? `${actionsRow.hours} hours` : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                User: {actionsRow.userId || '—'} • Project: {actionsRow.projectId || '—'}
              </Typography>
              {actionsRow.description ? <Typography variant="body2">{actionsRow.description}</Typography> : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {canEdit && actionsRow ? (
            <>
              <Button variant="outlined" onClick={() => openEdit(actionsRow)}>
                Edit
              </Button>
              <Button variant="outlined" onClick={async () => {
                try {
                  await apiClient.put(`/timesheets/${actionsRow.id}`, { status: 'APPROVED', approvedBy: 'demo_user_pm', approvedAt: new Date() });
                  setToast({ severity: 'success', message: 'Approved' });
                  setToastOpen(true);
                  setDetailsOpen(false);
                  refresh();
                } catch (e: any) {
                  setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Approve failed' });
                  setToastOpen(true);
                }
              }}>
                Approve
              </Button>
              <Button color="error" variant="outlined" onClick={async () => {
                try {
                  await apiClient.put(`/timesheets/${actionsRow.id}`, { status: 'REJECTED', approvedBy: 'demo_user_pm', approvedAt: new Date() });
                  setToast({ severity: 'success', message: 'Rejected' });
                  setToastOpen(true);
                  setDetailsOpen(false);
                  refresh();
                } catch (e: any) {
                  setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Reject failed' });
                  setToastOpen(true);
                }
              }}>
                Reject
              </Button>
            </>
          ) : null}
          <Button variant="contained" onClick={() => setDetailsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </PageLoader>
  );
};

export default TimesheetsPage;


